import { DEFAULT_REFERENCE, type IsolateArea, type ReferenceOptions } from '@mighty-map/map-engine';
import { create } from 'zustand';
import { useViewMode } from '../../state/viewMode';
import { browserStorage } from '../../lib/visibilityMemory';
import { DEFAULT_LEGEND_PREFS, parseLegendPrefs, type LegendPrefs } from '../../lib/layers-pro/legendModel';
import { loadWaterMemory } from '../../lib/layers-pro/water';
import type { TableFeature } from '../../lib/layers-pro/table';

/**
 * Layers Pro's own UI state. Layer visibility is the map store's (setNodeVisible); this holds what only the panel
 * knows: the open tab, expanded groups and row panels, per-layer transparency and labels, legend preferences, and the
 * table / export dialogs. Module state, so it survives the panel closing and reopening.
 */

/** `bim` is the Model tab: the one tab Layers Pro shows in BIM (production's `bim` tab). */
export type LayersTab = 'layers' | 'legend' | 'basemap' | 'bim';

/** The Symbology, Labels or Zoom levels editor, open on a layer. Zoom levels also opens on a group: its layers, under
 *  the group's title. */
export interface StyleEdit {
  layerId: string;
  mode: 'symbology' | 'labels' | 'zoom';
  layerIds?: readonly string[];
  title?: string;
}

export interface ExportRequest {
  layerId: string;
  /** Rows ticked in the attribute table when the dialog opened from it. */
  selection: readonly TableFeature[];
  selectedOnly: boolean;
}

/** The map isolated to a polygon drawn in Sketch (production's Isolate > Sketch): its Sketch item, name and shape as
 *  they were when chosen. */
export interface Isolate {
  key: string;
  label: string;
  area: IsolateArea;
}

export interface Toast {
  text: string;
  kind: 'info' | 'warn' | 'error';
}

const LEGEND_PREFS_KEY = 'mm_legend_prefs';

function loadLegendPrefs(): LegendPrefs {
  try {
    return parseLegendPrefs(browserStorage()?.getItem(LEGEND_PREFS_KEY) ?? null);
  } catch {
    return { ...DEFAULT_LEGEND_PREFS };
  }
}

const REFERENCE_KEY = 'mm_basemap_reference';

/** The Basemap tab's switches on the imagery's labels and roads, as last set on this browser. */
function loadReference(): ReferenceOptions {
  try {
    const raw: unknown = JSON.parse(browserStorage()?.getItem(REFERENCE_KEY) ?? 'null');
    if (!raw || typeof raw !== 'object') return { ...DEFAULT_REFERENCE };
    const r = raw as Record<string, unknown>;
    const d = DEFAULT_REFERENCE;
    const fade =
      typeof r['roadFade'] === 'number' ? Math.round(Math.max(20, Math.min(100, r['roadFade']))) : d.roadFade;
    return {
      places: typeof r['places'] === 'boolean' ? r['places'] : d.places,
      roads: typeof r['roads'] === 'boolean' ? r['roads'] : d.roads,
      roadTheme: r['roadTheme'] === 'dark' || r['roadTheme'] === 'amber' ? r['roadTheme'] : 'classic',
      roadFade: fade,
    };
  } catch {
    return { ...DEFAULT_REFERENCE };
  }
}

function saveLegendPrefs(p: LegendPrefs): void {
  try {
    browserStorage()?.setItem(LEGEND_PREFS_KEY, JSON.stringify(p));
  } catch {
    // Preferences are a convenience: blocked storage just means they do not persist.
  }
}

interface LayersProState {
  tab: LayersTab;
  query: string;
  /** Group open state the user set (group key -> open); unset groups use the config default. */
  groupsOpen: Record<string, boolean>;
  /** Row panels (legend, details, transparency) the user opened, by layer id. */
  panels: Record<string, boolean>;
  /** Details sections opened from the row menu, by layer id. */
  detailsOpen: Record<string, boolean>;
  /** 0..1 per layer; absent = fully opaque. */
  opacity: Record<string, number>;
  /** Layers whose labels the user switched off. */
  labelsOff: Record<string, boolean>;
  /** 0..100 water translucency (0 = as styled); persisted in the browser. */
  waterTranslucency: number;
  legend: LegendPrefs;
  /** The imagery's place names and roads (the Basemap tab's switches). */
  reference: ReferenceOptions;
  legendCollapsed: Record<string, boolean>;
  tableLayer: string | null;
  exportRequest: ExportRequest | null;
  styleEdit: StyleEdit | null;
  /** How many layers have zoom levels of their own set (the Reset strip shows while there are any). */
  zoomRanges: number;
  isolate: Isolate | null;
  toast: Toast | null;
  setTab: (tab: LayersTab) => void;
  setQuery: (query: string) => void;
  setGroupOpen: (key: string, open: boolean) => void;
  togglePanel: (layerId: string) => void;
  openDetails: (layerId: string) => void;
  setDetailsOpen: (layerId: string, open: boolean) => void;
  setOpacity: (layerId: string, opacity: number) => void;
  setWaterTranslucency: (percent: number) => void;
  setLabelsOff: (layerIds: readonly string[], off: boolean) => void;
  setLegend: (patch: Partial<LegendPrefs>) => void;
  setReference: (patch: Partial<ReferenceOptions>) => void;
  toggleLegendCollapsed: (key: string) => void;
  openTable: (layerId: string | null) => void;
  openExport: (request: ExportRequest | null) => void;
  openStyleEdit: (edit: StyleEdit | null) => void;
  setIsolate: (isolate: Isolate | null) => void;
  setToast: (toast: Toast | null) => void;
}

export const useLayersPro = create<LayersProState>()((set, get) => ({
  tab: 'layers',
  query: '',
  groupsOpen: {},
  panels: {},
  detailsOpen: {},
  opacity: {},
  labelsOff: {},
  waterTranslucency: loadWaterMemory().translucency,
  legend: loadLegendPrefs(),
  reference: loadReference(),
  legendCollapsed: {},
  tableLayer: null,
  exportRequest: null,
  styleEdit: null,
  zoomRanges: 0,
  isolate: null,
  toast: null,

  setTab: (tab) => set({ tab }),
  setQuery: (query) => set({ query }),
  setGroupOpen: (key, open) => set({ groupsOpen: { ...get().groupsOpen, [key]: open } }),
  togglePanel: (layerId) => set({ panels: { ...get().panels, [layerId]: !get().panels[layerId] } }),
  openDetails: (layerId) =>
    set({ panels: { ...get().panels, [layerId]: true }, detailsOpen: { ...get().detailsOpen, [layerId]: true } }),
  setDetailsOpen: (layerId, open) => set({ detailsOpen: { ...get().detailsOpen, [layerId]: open } }),
  setOpacity: (layerId, opacity) => set({ opacity: { ...get().opacity, [layerId]: opacity } }),
  setWaterTranslucency: (percent) => set({ waterTranslucency: Math.min(100, Math.max(0, Number(percent) || 0)) }),
  setLabelsOff: (layerIds, off) => {
    const next = { ...get().labelsOff };
    for (const id of layerIds) {
      if (off) next[id] = true;
      else delete next[id];
    }
    set({ labelsOff: next });
  },
  setReference: (patch) => {
    const reference = { ...get().reference, ...patch };
    try {
      browserStorage()?.setItem(REFERENCE_KEY, JSON.stringify(reference));
    } catch {
      // As the legend's preferences: blocked storage just means they do not persist.
    }
    set({ reference });
  },
  setLegend: (patch) => {
    const legend = { ...get().legend, ...patch };
    saveLegendPrefs(legend);
    set({ legend });
  },
  toggleLegendCollapsed: (key) =>
    set({ legendCollapsed: { ...get().legendCollapsed, [key]: !get().legendCollapsed[key] } }),
  openTable: (tableLayer) => set({ tableLayer }),
  openExport: (exportRequest) => set({ exportRequest }),
  openStyleEdit: (styleEdit) => set({ styleEdit }),
  setIsolate: (isolate) => set({ isolate }),
  setToast: (toast) => set({ toast }),
}));

/* Layers Pro follows the view, as production's does: coming into BIM it lands on the Model tab rather than whatever
 * was open, and on any other change of view it lands back on Layers (production's onViewMode). Subscribed at module
 * load, because the panel may well be closed when the view changes; the panel also derives the tab from the view, so
 * one opened in BIM shows the Model tab whatever was stored. */
useViewMode.subscribe((s, prev) => {
  if (s.mode !== prev.mode) useLayersPro.setState({ tab: s.mode === 'bim' ? 'bim' : 'layers' });
});
